import type { TFunction } from 'i18next';

import type { AdCardCopy } from './ad-card-summary';
import type { MarketingAreaId } from './areas';

/** An area's ad count and empty line. Literal `t()` keys per area, so the extractor harvests every one. */
const countCopy = (t: TFunction, area: MarketingAreaId, n: number): string => {
  switch (area) {
    case 'banner-kits':
      return n === 1 ? t('1 image banner') : t('{{n}} image banners', { n });
    case 'html5-banners':
      return n === 1 ? t('1 HTML5 banner') : t('{{n}} HTML5 banners', { n });
    case 'videos':
      return n === 1 ? t('1 video ad') : t('{{n}} video ads', { n });
    case 'playables':
      return n === 1 ? t('1 mini-game') : t('{{n}} mini-games', { n });
    case 'landing-pages':
      return n === 1 ? t('1 live landing page') : t('{{n}} live landing pages', { n });
  }
};

const noneCopy = (t: TFunction, area: MarketingAreaId): string => {
  switch (area) {
    case 'banner-kits':
      return t('No image banners yet');
    case 'html5-banners':
      return t('No HTML5 banners yet');
    case 'videos':
      return t('No video ads yet');
    case 'playables':
      return t('No mini-games yet');
    case 'landing-pages':
      return t('No live landing pages yet');
  }
};

/**
 * A Marketing hub card's copy: one literal `t()` key per id (and per area where the noun differs); the `switch` is
 * exhaustive, so a new id fails the typecheck until it has its copy.
 */
export const adCardCopy = (t: TFunction, area: MarketingAreaId, c: AdCardCopy): string => {
  switch (c.id) {
    case 'count':
      return countCopy(t, area, c.n);
    case 'none':
      return noneCopy(t, area);
    case 'generating':
      return t('Generating');
    case 'drafts':
      return c.n === 1 ? t('1 draft') : t('{{n}} drafts', { n: c.n });
  }
};
